import { DefaultMap, definedMap } from "@glideapps/ts-necessities";
import { AutoWatchable, Watchable } from "common/change-obserable";
import type { AppEnvironment } from "common/components/types";
import type { EminenceFlags, EminencesByApp } from "common/Database/eminence";
import { freeFlags } from "common/Database/eminence";
import type { ComponentType, FunctionComponent } from "react";
import { getOwnerEminenceFlags } from "store/types";
import type { Subtract } from "utility-types";
import { useAppID } from "./use-app-id";
import { useAppOwner } from "./use-app-owner";
import { useChangeObservable } from "./use-change-observable";
import { useDeepEqual } from "./use-deep-equal";

type BuilderOwnerEminenceFlags = DefaultMap<string, Watchable<EminencesByApp>>;
const allWatchedFlags: BuilderOwnerEminenceFlags = new DefaultMap(k => {
    return k !== "" ? new AutoWatchable({}, async () => getOwnerEminenceFlags(k), 60 * 1000) : new Watchable({});
});

// NOTE: If ownerID isn't specified, and the app owner's eminence has yet to be retrieved, this will return free flags.
export function getBuilderEminenceFlags(appID: string, ownerID: string | undefined): EminenceFlags {
    if (ownerID !== undefined) {
        return allWatchedFlags.get(ownerID).current[appID] ?? freeFlags;
    }

    // We don't know who the app belongs to, so we just check all owners.
    for (const watchable of allWatchedFlags.values()) {
        const flags = watchable.current[appID];
        if (flags !== undefined) {
            return flags;
        }
    }

    return freeFlags;
}

export async function updateBuilderEminenceFlags(ownerID: string): Promise<void> {
    const eminences = await getOwnerEminenceFlags(ownerID);
    allWatchedFlags.get(ownerID).current = eminences;
}

export function useBuilderEminenceFlags(appID: string): EminenceFlags {
    const owner = useAppOwner(appID);
    const ownerEminences = useChangeObservable(definedMap(owner, o => allWatchedFlags.get(o.id)));
    const appEminenceFlags = useDeepEqual(ownerEminences?.[appID]);
    return appEminenceFlags ?? freeFlags;
}

interface WithBuilderEminenceFlagsProps {
    readonly eminenceFlags: EminenceFlags;
}

interface WrapperProps {
    readonly appID: string;
    readonly appEnvironment: AppEnvironment;
}

interface Props extends Partial<WrapperProps>, WithBuilderEminenceFlagsProps {}

export const withBuilderEminenceFlags =
    <P extends Props>(Component: ComponentType<P>): FunctionComponent<Subtract<P, WithBuilderEminenceFlagsProps>> =>
    p => {
        const routerAppID = useAppID();
        const appID = p.appID ?? p.appEnvironment?.appID ?? routerAppID ?? "";
        const eminenceFlags = useBuilderEminenceFlags(appID);
        return <Component {...(p as P)} eminenceFlags={eminenceFlags} />;
    };
